import { Button, ButtonVariants } from '@components/button/Button';
import { errorToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { reportAppError } from '@shared/reportAppError';
import cx from 'classnames';
import { useEffect, useState } from 'react';
import typography from '~styles/typography.scss';
import { setLightspeedBusinessLocation } from './apiHelpers';
import { formatLocationAddress } from './formatLocationAddress';
import { useLightspeedBusinessLocations } from './hooks';
import styles from './LightspeedSettingsPage.scss';

const getLoadErrorMessage = (error: Error | null): string => {
  if (!error) {
    return '';
  }

  return error instanceof ApiError
    ? error.message
    : 'Could not load your Lightspeed locations.';
};

export interface LightspeedLocationPickerProps {
  restaurantId: string;
  onLinked: () => void;
}

export const LightspeedLocationPicker = ({
  restaurantId,
  onLinked,
}: LightspeedLocationPickerProps) => {
  const { data: locations, error } =
    useLightspeedBusinessLocations(restaurantId);
  const [linkingId, setLinkingId] = useState<string | null>(null);

  useEffect(() => {
    if (error && !(error instanceof ApiError)) reportAppError(error);
  }, [error]);

  const loadError = getLoadErrorMessage(error);

  const handleLink = async (businessLocationId: string) => {
    setLinkingId(businessLocationId);
    try {
      await setLightspeedBusinessLocation(restaurantId, businessLocationId);
      onLinked();
    } catch (error) {
      if (error instanceof ApiError && error.statusCode < 500) {
        errorToast({ message: error.message });
      } else {
        errorToast({
          message: 'Lightspeed could not link this location. Please try again.',
        });
        if (!(error instanceof ApiError)) reportAppError(error);
      }
    } finally {
      setLinkingId(null);
    }
  };

  return (
    <section className={styles.locationPicker} aria-labelledby="ls-locations">
      <h2 id="ls-locations" className={typography.h5}>
        Choose your Lightspeed location
      </h2>
      <p className={cx(typography.t2, styles.muted)}>
        Reservations sync to this location, and its orders show up in Peak.
      </p>
      {loadError && <p role="alert">{loadError}</p>}
      {!locations && !loadError && <p>Loading locations…</p>}
      {locations?.length === 0 && (
        <p>
          Your Lightspeed account has no locations available. Check that you
          signed in with the right Lightspeed account.
        </p>
      )}
      {locations && locations.length > 0 && (
        <ul className={styles.locationList}>
          {locations.map((location) => (
            <li
              key={location.businessLocationId}
              className={styles.locationRow}
            >
              <div>
                <p className={cx(typography.t1, styles.locationName)}>
                  {location.name ?? location.businessLocationId}
                </p>
                {formatLocationAddress(location) && (
                  <p className={cx(typography.t2, styles.muted)}>
                    {formatLocationAddress(location)}
                  </p>
                )}
              </div>
              <Button
                type="button"
                variant={ButtonVariants.Primary}
                onClick={() => void handleLink(location.businessLocationId)}
                isDisabled={linkingId !== null}
              >
                {`Use ${location.name ?? location.businessLocationId}`}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
